import { useEffect, useMemo } from 'react'
import {
	type BothFullMissionStates,
	type JuniorPlusMissionFullState,
	type JuniorMissionFullState,
	type SingleCreativeCanvasStation,
	type VocalTrack,
	getCurrentVocalTrackFromMission,
} from '@mission.io/mission-toolkit'
import {
	DEFAULT_VOLUME,
	DEFAULT_VOLUME_RATIOS,
} from '@mission.io/mission-toolkit/constants'
import type { ImageWithText } from '@mission.io/mission-toolkit/actions'
import {
	useIsJunior,
	useIsJuniorPlus,
	useMissionState,
	useStaticQuestions,
	useLocalDispatch,
	useLocalState,
	useShowAccessibility,
	useControlledSoundEffects,
	useVolumes,
} from './provider'
import {
	getASLVideos,
	setPlayerShaking,
	setShowDeathScreen,
	type PlayerState,
} from './reducer'
import type { EffectId } from '@mission.io/mission-player'
import type { ClientQuestion as Question } from '@mission.io/question-toolkit'
import {
	AUDIO_DUCK_SOURCES,
	VOCAL_TRACK_DISPLAY_IDS,
	type ASLVideo,
} from '../utility'
import { getScreen, getDeathState } from './missionState'

/**
 * A hook which allows us to get a value back from the shared mission state given a selector function for that value
 * @param {?BothFullMissionStates) => T} selector
 * @param {T} T
 */
export function useMissionStateSelector<T>(
	selector: (?BothFullMissionStates) => T
): T {
	const missionState = useMissionState()
	return selector(missionState)
}

/**
 * A hook which allows us to get a value back from the junior mission state given a selector function for that value.
 * @param {?BothFullMissionStates) => T} selector
 * @param {T} T
 */
export function useJuniorStateSelector<T>(
	selector: (?JuniorMissionFullState) => T
): T {
	const isJunior = useIsJunior()
	const missionState = useMissionState()
	if (isJunior) {
		return selector(((missionState: any): JuniorMissionFullState))
	} else return selector(null)
}

/**
 * A hook which allows us to get a value back from the standard (junior plus) mission state given a selector function for that value
 * @param {?BothFullMissionStates) => T} selector
 * @param {T} T
 */
export function useJuniorPlusStateSelector<T>(
	selector: (?JuniorPlusMissionFullState) => T
): T {
	const isJuniorPlus = useIsJuniorPlus()
	const missionState = useMissionState()
	if (isJuniorPlus) {
		return selector(((missionState: any): JuniorPlusMissionFullState))
	} else return selector(null)
}

export function useLocalStateSelector<T>(selector: PlayerState => T): T {
	const localState = useLocalState()
	return selector(localState)
}

/**
 * Returns the current question slot question if it exists
 */
export function useQuestionSlotQuestion(): ?Question {
	const questions = useStaticQuestions()
	const screen = useMissionStateSelector(getScreen)
	if (!screen || screen.type !== 'QUESTION_SLOT' || !questions) return null
	const questionId = screen?.questionId
	const question = questions.find(item => item._id === questionId)
	return question
}

const SHAKING_DURATION = 4000

/**
 * This hook watches for when the crew 'dies' and will take care of any extra effects
 * we want to have occur on the app.
 */
export function useOnDeath() {
	const deathState = useMissionStateSelector(getDeathState)
	const localDispatch = useLocalDispatch()
	useEffect(() => {
		if (deathState) {
			if (deathState.status === 'SHIP_DEATH') {
				localDispatch(setPlayerShaking(true))
				const timeoutId = setTimeout(() => {
					localDispatch(setPlayerShaking(false))
					localDispatch(setShowDeathScreen(true))
				}, SHAKING_DURATION)
				return () => clearTimeout(timeoutId)
			}
			if (deathState.status === 'FAILED_STATE') {
				localDispatch(setShowDeathScreen(true))
			} else {
				localDispatch(setPlayerShaking(false))
				localDispatch(setShowDeathScreen(false))
			}
		}
	}, [deathState, localDispatch])
}

export function useCurrentASLVideo(): ?ASLVideo {
	const showAccessibility = useShowAccessibility()
	const currentASLVideos = useLocalStateSelector(getASLVideos)
	if (showAccessibility) {
		return currentASLVideos[0] || null
	}
	return null
}

export function useIsShowingASLVideo(): boolean {
	const currentVideo = useCurrentASLVideo()
	return Boolean(currentVideo)
}

/**
 * Returns a control for a sound effect id if one exists
 * @param {EffectId} effectId
 */
export function useControlledSoundEffect(
	effectId: EffectId
): ?{ isPlaying: boolean } {
	const controlledSoundEffects = useControlledSoundEffects()
	return controlledSoundEffects[effectId]
}

/**
 * Gets the current vocal track, returns null if the audio has not been ducked first.
 */
export function useCurrentVocalTrack(): ?VocalTrack {
	const missionState = useMissionState()
	const playerState = useLocalState()
	if (
		playerState.audioDucking['VOCAL_TRACK'] != null &&
		playerState.audioDucking['VOCAL_TRACK'] <
			AUDIO_DUCK_SOURCES.VOCAL_TRACK.PERCENTAGE
	) {
		return null
	}
	return getCurrentVocalTrackFromMission(missionState)
}

/**
 * Determines if the vocal track requires a character overlay
 */
export function useIsCharacterOverlay(): boolean {
	const currentVocalTrack = useCurrentVocalTrack()
	return currentVocalTrack?.display === VOCAL_TRACK_DISPLAY_IDS.SHOW_CHARACTER
}

/**
 * Provides the current volume of sound type: EFFECTS, VIDEO, VOCAL TRACK, MUSIC, AMBIANCE
 * based on audio ducking by default.
 * @param {VolumeType} key
 * @param {?boolean} useAudioDucking defaults to true
 */
export function useVolumeOf(
	key: $Keys<typeof DEFAULT_VOLUME_RATIOS>,
	useAudioDucking?: boolean = true
): number {
	const volumes = useVolumes()
	const audioDucking = useLocalStateSelector(state => state.audioDucking)
	let volume = volumes
		? volumes[key]
		: DEFAULT_VOLUME * DEFAULT_VOLUME_RATIOS[key]
	if (useAudioDucking) {
		// get largest audio ducking value
		const largestAudioDuckingValue = Object.keys(audioDucking).reduce(
			(largest, source) =>
				audioDucking[source] > largest ? audioDucking[source] : largest,
			0
		)
		const volumeRatio = 1 - largestAudioDuckingValue
		volume = volume * volumeRatio
	}
	return volume
}

/**
 * Gets the data for a specific creative canvas station
 */
export function useCreativeCanvasData(
	canvasId: string
): null | SingleCreativeCanvasStation {
	return useJuniorPlusStateSelector((state: ?JuniorPlusMissionFullState) => {
		return state?.stationData.creativeCanvas.canvases[canvasId] || null
	})
}

export type CanvasMedia =
	| { type: 'BACKGROUND_IMAGE', url: string, _id: string }
	| { ...ImageWithText, _id: string }

/**
 * Get any image media that will be displayed on the canvas. For now this includes background media and design media.
 * @param {string} documentId
 * @param {string} canvasId
 * @returns {Array<CanvasMedia>}  array of objects containing the url and type of the media
 */
export function useCreativeCanvasStaticMedia(
	documentId: string,
	canvasId: string
): Array<CanvasMedia> {
	const creativeCanvas = useCreativeCanvasData(canvasId)
	const team = creativeCanvas?.teams.find(
		team => team.document.id === documentId
	)
	const canvasData = creativeCanvas?.canvasData
	const teamCanvasData = team
		? creativeCanvas?.canvasData.issues[team.issueId]
		: null

	return useMemo(() => {
		const media = []
		if (teamCanvasData?.backgroundMedia) {
			media.push({
				...teamCanvasData.backgroundMedia,
				type: 'BACKGROUND_IMAGE',
			})
		} else if (canvasData?.backgroundMedia) {
			media.push({
				...canvasData.backgroundMedia,
				type: 'BACKGROUND_IMAGE',
			})
		}
		if (canvasData?.designMedia?.length) {
			media.push(...canvasData.designMedia)
		}
		if (teamCanvasData?.designMedia?.length) {
			media.push(...teamCanvasData.designMedia)
		}
		return media
	}, [canvasData, teamCanvasData])
}
